// Host detection for the Foote Cattle Co platform (foote.raisedbeef.com).
//
// Foote is the first NAMED-CUSTOMER platform: RaisedBeef's tools, Foote's
// branding, on Foote's own subdomain. It is not a new product — it is product
// 1's market tools wearing a customer's colours (see PRODUCTS.md).
//
// ⚠️ Foote logins are PROVISIONED, NOT PURCHASED (owner, 2026-09-18:
// "Paying will be done manually"). There is no signup, no pricing page and no
// checkout on this host, and there must never be one added by reflex. A Foote
// person who is entitled is entitled because someone at RaisedBeef made it so.

export const FOOTE_HOSTS = ["foote.raisedbeef.com"];

/** Where the preview override is remembered. See STICKINESS below. */
const PREVIEW_KEY = "rb.site.foote";

/**
 * The Foote Cattle Co platform host. Local/dev preview: `?site=foote`.
 *
 * ⚠️ STICKINESS IS NOT A CONVENIENCE — WITHOUT IT THE PREVIEW IS UNUSABLE.
 *
 * The real subdomain answers on hostname alone and never needs the override.
 * Everywhere else — a Vercel preview, localhost — `?site=foote` is the only
 * thing that makes these routes exist. But this is a single-page app: the
 * routes are decided on every render, and the moment React Router navigates
 * from "/?site=foote" to "/foote" the query string is gone, this function
 * starts answering false, the Foote routes vanish from the tree, and the
 * person is dropped on a 404. Every button on the platform dead-ends on the
 * first click.
 *
 * That is exactly what happened: the owner reviewed page one by typing the URL
 * fresh each time, so it looked fine, and the fault only showed up once there
 * were buttons to press. So the override is remembered for the browser tab
 * once it has been seen.
 *
 * This is not a security boundary and must never be mistaken for one. It
 * changes which routes render, nothing else. What a Foote person may actually
 * READ is decided by RLS and the SECURITY DEFINER RPCs on the server, which do
 * not care what this function thinks.
 */
export function isFooteHost(): boolean {
  if (typeof window === "undefined") return false;
  if (FOOTE_HOSTS.includes(window.location.hostname)) return true;

  try {
    if (new URLSearchParams(window.location.search).get("site") === "foote") {
      // Remember it, so a client-side navigation that drops the query does not
      // make the whole platform disappear mid-session.
      window.sessionStorage?.setItem(PREVIEW_KEY, "1");
      return true;
    }
    // sessionStorage is per-tab and dies with it, so a preview never leaks into
    // a later ordinary visit to raisedbeef.com in a new tab.
    return window.sessionStorage?.getItem(PREVIEW_KEY) === "1";
  } catch {
    // Private mode, blocked storage, or a malformed query. Fall back to the
    // hostname answer rather than throwing inside a render.
    return false;
  }
}

/** Testing seam — forget the remembered preview override. */
export function clearFootePreview(): void {
  try {
    window.sessionStorage?.removeItem(PREVIEW_KEY);
  } catch {
    /* ignore */
  }
}
